import { type TFunction } from '!/i18n'
import { Day, State, rangePosition, type RangePosition } from '$/epg/programs'
import {
  DATE_FORMATS,
  formatDateDistanceToNow,
  formatDateDuration,
  formatDateToLabelInfo,
  formatStartTime,
  timeWithin,
} from '~/shared/helpers/date'
import {
  type EpgSidebarInfo,
  type EpgSidebarLocalizedInfo,
  type EpgSidebarProgramInfo,
  type EpgSidebarProgramLocalizedInfo,
} from './index.h'

const formatTodayFutureProgramLabel = ({
  untilMs,
}: Readonly<{
  untilMs: number
}>): string => {
  const { hours, minutes } = timeWithin(untilMs)
  if (!hours && !minutes) return ''
  return formatDateDistanceToNow({ minutes, hours })
}

const formatStartEndSidebar = ({
  t,
  actualStartMs,
  actualStopMs,
  leftMs,
  untilMs,
  position,
}: Readonly<{
  t: TFunction
  actualStartMs: number
  actualStopMs: number
  leftMs: number
  untilMs: number
  position: RangePosition
}>): string[] => {
  const startEndLabel = formatDateToLabelInfo(actualStartMs, actualStopMs)

  // for live program format labels as [live, start-end, left time]
  if (position.state === State.Ongoing) {
    const { hours, minutes } = timeWithin(leftMs)
    const formattedDuration = formatDateDuration({
      duration: { hours, minutes },
      short: true,
    })

    const labelLeft = t('time_left', {
      time: formattedDuration,
    })
    const live = t('live')
    return [live, startEndLabel, labelLeft]
  }

  // for today future program format labels as [today, start-end, until time]
  if (position.state === State.Upcoming && position.starts === Day.Today) {
    const today = t('today')
    const labelUntil = formatTodayFutureProgramLabel({ untilMs })
    return [`${today}, ${startEndLabel}`, labelUntil]
  }

  // for tomorrow program format labels as [tomorrow, start-end]
  if (position.starts === Day.Tomorrow) {
    const tomorrow = t('tomorrow')
    return [tomorrow, startEndLabel]
  }

  // for all other cases (past, future) format labels as [date, start-end]
  const date = formatStartTime(actualStartMs, DATE_FORMATS.EEEE_DD_MM)
  return [date, startEndLabel]
}

export const formatProgramToLocalizedView =
  ({ t, now }: { t: TFunction; now: number }) =>
  (info: EpgSidebarProgramInfo): EpgSidebarProgramLocalizedInfo => {
    const position = rangePosition([info.actualStartMs, info.actualStopMs], now)
    return {
      ...info,
      isLive: position.state === State.Ongoing,
      labels: formatStartEndSidebar({
        t,
        actualStartMs: info.actualStartMs,
        actualStopMs: info.actualStopMs,
        leftMs: info.actualStopMs - now,
        untilMs: info.actualStartMs - now,
        position,
      }),
    }
  }

export const formatToLocalizedView =
  ({ t }: { t: TFunction }) =>
  (info: EpgSidebarInfo): EpgSidebarLocalizedInfo => ({
    ...info,
    titleDirectors: t('directors'),
    titleStars: t('stars'),
    titleUpcoming: t('upcoming'),
  })
